import { useRef, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountDetailSchema,
  AssetSchema,
  ClientSchema,
  CustomerInvoiceDetailSchema,
  CustomerInvoiceSchema,
  EmployeeSchema,
  OrganizationSchema,
  ProjectSchema,
  type ClientDto,
  type CustomerInvoiceDetailDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { getData, getPage, newIdempotencyKey, patchJson, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { useUrlList } from '../../shared/ui/url-list';
import { ApprovalPanel, workflowHistoryKey } from '../../shared/workflow/ApprovalPanel';
import { ActiveClientPicker } from '../crm/common';
import { refreshAfterPosting } from '../finance/cache';
import {
  DimensionRef,
  Facts,
  JournalRef,
  RecordFailure,
  RecordLink,
  useDimensionNames,
} from '../finance/detail';
import {
  businessToday,
  emptyLine,
  lineProblems,
  lineRequests,
  linesComplete,
  LinesEditor,
  type LineDraft,
} from '../finance/line-editor';
import { MasterSelect, useActiveMasters } from '../finance/lookups';
import { sum } from '../finance/money';
import { RefusalState } from '../finance/refusal';
import { SearchFilterBar } from '../../shared/ui/filters';

export const CUSTOMER_INVOICES = '/api/v1/finance/customer-invoices';
const INVOICE = 'finance.customer_invoice';

/**
 * Customer invoices (FRD-FIN-057 to FIN-059, FIN-062): every invoice with its status, due date,
 * payment status, outstanding and overdue indication, filtered on the server with the filters in
 * the URL. Drafts are prepared here; issue happens on the invoice's own page.
 */
export function CustomerInvoicesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, `${INVOICE}:create`);
  const list = useUrlList({ pageSize: 50 });
  const params = list.apiParams(
    {
      search: 'search',
      status: 'status',
      payment: 'payment_status',
      overdue: 'overdue',
      open: 'open',
      client: 'client_id',
      from: 'invoice_date_from',
      to: 'invoice_date_to',
    },
    '-invoice_date',
  );
  const invoices = useQuery({
    queryKey: ['customer-invoices', params.toString()],
    queryFn: () => getPage(`${CUSTOMER_INVOICES}?${params.toString()}`, CustomerInvoiceSchema),
  });
  const client = useQuery({
    queryKey: ['crm', 'client', list.get('client')],
    enabled: list.get('client') !== '' && hasCapability(session.data, 'crm.client:view'),
    queryFn: () => getData(`/api/v1/crm/clients/${list.get('client')}`, ClientSchema),
  });
  const select = (label: string, key: string, options: readonly [string, string][]) => (
    <TextField
      select
      size="small"
      label={label}
      value={list.get(key)}
      sx={{ minWidth: 160 }}
      onChange={(e) => {
        list.set(key, e.target.value);
      }}
    >
      <MenuItem value="">Any</MenuItem>
      {options.map(([value, text]) => (
        <MenuItem key={value} value={value}>
          {text}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Customer invoices"
        description="Invoices to clients with what is outstanding on each."
        actions={
          <>
            <ExportButton recordType={INVOICE} href={exportHref(CUSTOMER_INVOICES, params)} />
            {canCreate && (
              <Button
                component={RouterLink}
                to="/customer-invoices/new"
                variant="contained"
                startIcon={<AddIcon />}
              >
                New invoice
              </Button>
            )}
          </>
        }
      />
      <Box role="search" aria-label="Filter the invoices">
        <SearchFilterBar
          label="Search number or description"
          value={list.get('search')}
          onChange={(value) => {
            list.set('search', value);
          }}
        >
          {select('Status', 'status', [
            ['draft', 'Draft'],
            ['issued', 'Issued'],
          ])}
          {select('Payment', 'payment', [
            ['unpaid', 'Unpaid'],
            ['part_paid', 'Part paid'],
            ['paid', 'Paid'],
          ])}
          {select('Outstanding', 'open', [['true', 'Outstanding only']])}
          {select('Overdue', 'overdue', [['true', 'Overdue only']])}
          {hasCapability(session.data, 'crm.client:view') && (
            <Box sx={{ minWidth: 240 }}>
              <ActiveClientPicker
                label="Client"
                value={client.data ?? null}
                onChange={(value) => {
                  list.set('client', value?.id ?? '');
                }}
              />
            </Box>
          )}
          <TextField
            size="small"
            type="date"
            label="From"
            value={list.get('from')}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              list.set('from', e.target.value);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={list.get('to')}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              list.set('to', e.target.value);
            }}
          />
        </SearchFilterBar>
      </Box>
      <Section>
        {invoices.isPending && <LoadingState label="Loading invoices" />}
        {invoices.isError && <ErrorState message={invoices.error.message} />}
        {invoices.data?.items.length === 0 && <EmptyState title="No invoices found" />}
        {invoices.data !== undefined && invoices.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Customer invoices">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.data.items.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/customer-invoices/${invoice.id}`}>
                        {invoice.documentNo ?? `Draft #${invoice.id}`}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{invoice.clientLabel ?? '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.invoiceDate}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.dueDate ?? '—'}</TableCell>
                    <TableCell align="right">{invoice.grossTotal}</TableCell>
                    <TableCell align="right">{invoice.outstandingAmount}</TableCell>
                    <TableCell>
                      {invoice.status === 'issued' ? (
                        <StatusChip status={invoice.paymentStatus} />
                      ) : (
                        '—'
                      )}
                      {invoice.overdue && <StatusChip status="overdue" />}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={invoice.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={invoices.data.total}
              page={list.page}
              rowsPerPage={list.pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, next) => {
                list.setPage(next);
              }}
              onRowsPerPageChange={(e) => {
                list.setPageSize(Number.parseInt(e.target.value, 10));
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}

/**
 * Prepares or amends a draft invoice (`/customer-invoices/new`, `/customer-invoices/:id/edit`):
 * the client, dates, the receivable's cost centre where its account needs one, and lines that
 * each name a revenue account and the dimensions that account requires (FRD-FIN-059). VAT is
 * shown as the API estimates it and determined at issue (FRD-VAT-021).
 */
export function CustomerInvoiceEditorPage() {
  const { id } = useParams();
  const existing = useQuery({
    queryKey: ['customer-invoice', id ?? 'new'],
    enabled: id !== undefined,
    queryFn: () => getData(`${CUSTOMER_INVOICES}/${id ?? ''}`, CustomerInvoiceDetailSchema),
  });
  const back = (
    <Button
      component={RouterLink}
      to={id === undefined ? '/customer-invoices' : `/customer-invoices/${id}`}
      startIcon={<ArrowBackIcon />}
    >
      {id === undefined ? 'All invoices' : 'Back to the invoice'}
    </Button>
  );
  if (id !== undefined && existing.isPending) return <LoadingState label="Loading the invoice" />;
  if (id !== undefined && existing.isError) {
    return (
      <RecordFailure title="Customer invoice" noun="invoice" error={existing.error} back={back} />
    );
  }
  return <InvoiceEditor existing={existing.data ?? null} back={back} />;
}

function InvoiceEditor({
  existing,
  back,
}: {
  existing: CustomerInvoiceDetailDto | null;
  back: ReactNode;
}) {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = businessToday(session.data?.businessTimezone);
  const canSeeCostCentres = hasCapability(session.data, 'organization.cost_center:view');
  const costCentres = useActiveMasters('/api/v1/organization/cost-centers', canSeeCostCentres);
  const organization = useQuery({
    queryKey: ['organization'],
    queryFn: () => getData('/api/v1/organization/organization', OrganizationSchema),
  });
  const existingClient = useQuery({
    queryKey: ['crm', 'client', existing?.invoice.clientId ?? ''],
    enabled: existing !== null,
    queryFn: () => getData(`/api/v1/crm/clients/${existing?.invoice.clientId ?? ''}`, ClientSchema),
  });
  const [client, setClient] = useState<ClientDto | null>(null);
  const [invoiceDate, setInvoiceDate] = useState(existing?.invoice.invoiceDate ?? today);
  const [taxDate, setTaxDate] = useState(existing?.invoice.taxDate ?? today);
  const [dueDate, setDueDate] = useState(existing?.invoice.dueDate ?? '');
  const [costCenterId, setCostCenterId] = useState(existing?.invoice.costCenterId ?? '');
  const [description, setDescription] = useState(existing?.invoice.description ?? '');
  const [lines, setLines] = useState<LineDraft[]>(() => (existing === null ? [emptyLine(1)] : []));
  const [loaded, setLoaded] = useState(existing === null);
  const idempotencyKey = useRef(newIdempotencyKey());

  // An existing draft's lines are rebuilt with their accounts, so the editor shows what it holds.
  if (!loaded && existing !== null) {
    setLoaded(true);
    void Promise.all(
      existing.lines.map(async (line, index) => ({
        ...emptyLine(index + 1),
        description: line.description,
        netAmount: line.netAmount,
        costCenterId: line.costCenterId ?? '',
        locationId: line.locationId ?? '',
        account: await getData(`/api/v1/ledger/accounts/${line.accountId}`, AccountDetailSchema)
          .then((d) => d.account)
          .catch(() => null),
        employee:
          line.employeeId === null
            ? null
            : await getData(`/api/v1/hr/employees/${line.employeeId}`, EmployeeSchema).catch(
                () => null,
              ),
        project:
          line.projectId === null
            ? null
            : await getData(`/api/v1/projects/${line.projectId}`, ProjectSchema).catch(() => null),
        asset:
          line.assetId === null
            ? null
            : await getData(`/api/v1/assets/assets/${line.assetId}`, AssetSchema).catch(() => null),
      })),
    ).then(setLines);
  }
  const chosenClient = client ?? existingClient.data ?? null;
  const save = useMutation({
    mutationFn: () => {
      const body = {
        clientId: chosenClient?.id ?? '',
        organizationId: organization.data?.id ?? '',
        invoiceDate,
        taxDate,
        ...(dueDate !== '' ? { dueDate } : {}),
        ...(costCenterId !== '' ? { costCenterId } : {}),
        ...(description.trim() !== '' ? { description: description.trim() } : {}),
        lines: lineRequests(lines, true),
      };
      return existing === null
        ? postJson(
            CUSTOMER_INVOICES,
            body,
            CustomerInvoiceDetailSchema,
            session.data?.csrfToken ?? '',
            {
              idempotencyKey: idempotencyKey.current,
            },
          )
        : patchJson(
            `${CUSTOMER_INVOICES}/${existing.invoice.id}`,
            body,
            CustomerInvoiceDetailSchema,
            session.data?.csrfToken ?? '',
            existing.invoice.version,
          );
    },
    onSuccess: async (saved) => {
      await refreshAfterPosting(queryClient);
      await navigate(`/customer-invoices/${saved.invoice.id}`);
    },
  });
  const problems = [
    ...(chosenClient === null ? ['Choose the client.'] : []),
    ...(dueDate !== '' && dueDate < invoiceDate
      ? ['The due date is before the invoice date.']
      : []),
    ...lineProblems(lines, true),
  ];
  const ready = linesComplete(lines) && problems.length === 0 && organization.data !== undefined;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={existing === null ? 'New customer invoice' : 'Edit draft invoice'}
        description="A draft; it receives its tax-invoice number when it is issued."
        actions={back}
      />
      <Section title="Invoice">
        <Stack spacing={2} sx={{ p: 2 }}>
          <Box sx={{ maxWidth: 480 }}>
            <ActiveClientPicker label="Client" required value={chosenClient} onChange={setClient} />
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              type="date"
              label="Invoice date"
              required
              value={invoiceDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setInvoiceDate(e.target.value);
              }}
            />
            <TextField
              type="date"
              label="Tax date"
              required
              value={taxDate}
              helperText="The tax point (FRD-VAT-024)"
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setTaxDate(e.target.value);
              }}
            />
            <TextField
              type="date"
              label="Due date"
              value={dueDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setDueDate(e.target.value);
              }}
            />
            <MasterSelect
              label="Receivable cost centre"
              value={costCenterId}
              disabled={!canSeeCostCentres}
              helperText="Where the receivable account requires one"
              options={costCentres.data?.items ?? []}
              onChange={setCostCenterId}
            />
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
        </Stack>
      </Section>
      <Section title="Lines">
        <Stack spacing={2} sx={{ p: 2 }}>
          <LinesEditor lines={lines} onChange={setLines} clientFromHeader />
          <Typography variant="subtitle2">
            Net total {sum(lines.map((l) => l.netAmount))}
          </Typography>
        </Stack>
      </Section>
      {problems.length > 0 && (
        <Alert severity="info" variant="outlined">
          <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      {save.isError && <RefusalState error={save.error} />}
      <Box>
        <Button
          variant="contained"
          disabled={!ready || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {existing === null ? 'Save draft' : 'Save changes'}
        </Button>
      </Box>
    </Stack>
  );
}

/**
 * One customer invoice (`/customer-invoices/:id`): its header, lines with their retained VAT, the
 * issued tax-invoice snapshot, what was credited and received, and the actions its state and the
 * session's permissions allow — Edit and Submit on a draft, Issue (after approval where a route is
 * configured), and from an issued invoice a credit note or a receipt. Approval history, documents
 * and the audit trail follow.
 */
export function CustomerInvoiceDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const names = useDimensionNames();
  const can = (capability: string) => hasCapability(session.data, capability);
  const [dialog, setDialog] = useState<'submit' | 'issue' | null>(null);
  const detail = useQuery({
    queryKey: ['customer-invoice', id],
    queryFn: () => getData(`${CUSTOMER_INVOICES}/${id}`, CustomerInvoiceDetailSchema),
  });
  const refresh = async () => {
    await refreshAfterPosting(queryClient);
    await queryClient.invalidateQueries({ queryKey: workflowHistoryKey(INVOICE, id) });
    await queryClient.invalidateQueries({ queryKey: ['audit'] });
  };
  const act = useMutation({
    mutationFn: (input: { kind: 'submit' | 'issue'; version: number }) =>
      postJson(
        `${CUSTOMER_INVOICES}/${id}/${input.kind}`,
        {},
        CustomerInvoiceDetailSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: input.version },
      ),
    onSuccess: async () => {
      setDialog(null);
      await refresh();
    },
  });
  const back = (
    <Button component={RouterLink} to="/customer-invoices" startIcon={<ArrowBackIcon />}>
      All invoices
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the invoice" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Customer invoice" noun="invoice" error={detail.error} back={back} />
    );
  }
  const { invoice, client, journal, lines, creditNotes, receipts } = detail.data;
  const draft = invoice.status === 'draft';
  const editable =
    draft && ['not_submitted', 'returned', 'rejected'].includes(invoice.approvalStatus);
  const title = invoice.documentNo ?? `Draft invoice #${invoice.id}`;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={title}
        description={`Customer invoice · ${client.summary ?? ''}`}
        actions={
          <>
            {back}
            {editable && can(`${INVOICE}:change`) && (
              <Button
                component={RouterLink}
                to={`/customer-invoices/${invoice.id}/edit`}
                variant="outlined"
              >
                Edit
              </Button>
            )}
            {editable && can(`${INVOICE}:create`) && (
              <Button
                variant="outlined"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('submit');
                }}
              >
                Submit for approval
              </Button>
            )}
            {draft && invoice.approvalStatus !== 'submitted' && can(`${INVOICE}:post`) && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('issue');
                }}
              >
                Issue
              </Button>
            )}
            {!draft && (
              <Button
                component={RouterLink}
                to={`/customer-invoices/${invoice.id}/print`}
                variant="outlined"
                startIcon={<PrintIcon />}
              >
                Tax invoice
              </Button>
            )}
            {!draft && can('finance.customer_credit_note:create') && (
              <Button
                component={RouterLink}
                to={`/customer-credit-notes/new?invoice=${invoice.id}`}
                variant="outlined"
              >
                Credit note
              </Button>
            )}
            {!draft &&
              invoice.outstandingAmount !== '0.00' &&
              can('finance.customer_receipt:create') && (
                <Button
                  component={RouterLink}
                  to={`/customer-receipts/new?client=${invoice.clientId}&invoice=${invoice.id}`}
                  variant="outlined"
                >
                  Record receipt
                </Button>
              )}
          </>
        }
      />
      {act.isError && <RefusalState error={act.error} />}
      <Section title="Invoice">
        <Facts
          label="Invoice details"
          facts={[
            ['Status', <StatusChip key="s" status={invoice.status} />],
            ...(draft
              ? ([['Approval', <StatusChip key="a" status={invoice.approvalStatus} />]] as [
                  string,
                  ReactNode,
                ][])
              : []),
            [
              'Client',
              can('crm.client:view') ? (
                <RecordLink key="c" to={`/crm/clients/${invoice.clientId}`}>
                  {client.summary ?? invoice.clientId}
                </RecordLink>
              ) : (
                (client.summary ?? '—')
              ),
            ],
            ['Invoice date', invoice.invoiceDate],
            ['Tax date', invoice.taxDate],
            ['Due date', invoice.dueDate ?? '—'],
            ['Receivable cost centre', names.costCentre(invoice.costCenterId)],
            ['Net', invoice.netTotal],
            [
              'VAT',
              invoice.status === 'issued'
                ? `${invoice.vatTotal}${invoice.vatCharged ? ' (charged)' : ' (retained, not charged: not VAT registered)'}`
                : `${invoice.vatTotal} (estimate; determined at issue)`,
            ],
            ['Total', invoice.grossTotal],
            ...(draft
              ? []
              : ([
                  ['Credited', invoice.creditedAmount],
                  ['Received', invoice.receivedAmount],
                  ['Outstanding', invoice.outstandingAmount],
                  [
                    'Payment',
                    <Stack key="p" direction="row" spacing={1}>
                      <StatusChip status={invoice.paymentStatus} />
                      {invoice.overdue && <StatusChip status="overdue" />}
                    </Stack>,
                  ],
                  ['Journal', <JournalRef key="j" journal={journal} />],
                ] as [string, ReactNode][])),
            ['Description', invoice.description ?? '—'],
          ]}
        />
      </Section>
      <Section title="Lines">
        <Table size="small" aria-label="Invoice lines">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Dimensions</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell>VAT treatment</TableCell>
              <TableCell align="right">Rate %</TableCell>
              <TableCell align="right">VAT</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>{line.lineNo}</TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {line.accountCode === null
                    ? '—'
                    : `${line.accountCode} — ${line.accountName ?? ''}`}
                </TableCell>
                <TableCell>
                  {line.costCenterId !== null && <div>{names.costCentre(line.costCenterId)}</div>}
                  {line.projectId !== null && (
                    <div>
                      <DimensionRef kind="project" id={line.projectId} />
                    </div>
                  )}
                  {line.assetId !== null && (
                    <div>
                      <DimensionRef kind="asset" id={line.assetId} />
                    </div>
                  )}
                  {line.employeeId !== null && <div>{names.employee(line.employeeId)}</div>}
                </TableCell>
                <TableCell align="right">{line.netAmount}</TableCell>
                <TableCell>{line.vat?.treatment.replace(/_/g, ' ') ?? '—'}</TableCell>
                <TableCell align="right">{line.vat?.rateApplied ?? '—'}</TableCell>
                <TableCell align="right">{line.vat?.vatAmount ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      {!draft && (
        <Section title="Credits and receipts">
          {creditNotes.length === 0 && receipts.length === 0 ? (
            <EmptyState title="Nothing credited or received yet" />
          ) : (
            <Table size="small" aria-label="Credits and receipts">
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditNotes.map((note) => (
                  <TableRow key={`c-${note.id}`}>
                    <TableCell>
                      <RecordLink to={`/customer-credit-notes/${note.id}`}>
                        Credit note {note.documentNo ?? `draft #${note.id}`}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{note.creditNoteDate}</TableCell>
                    <TableCell align="right">{note.grossTotal}</TableCell>
                    <TableCell>
                      <StatusChip status={note.status} />
                    </TableCell>
                  </TableRow>
                ))}
                {receipts.map((receipt) => (
                  <TableRow key={`r-${receipt.receiptId}-${receipt.allocationDate}`}>
                    <TableCell>
                      <RecordLink to={`/customer-receipts/${receipt.receiptId}`}>
                        Receipt {receipt.documentNo}
                        {receipt.fromAdvance ? ' (from advance)' : ''}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{receipt.allocationDate}</TableCell>
                    <TableCell align="right">{receipt.allocatedAmount}</TableCell>
                    <TableCell>
                      <StatusChip status="posted" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
      <ApprovalPanel recordType={INVOICE} recordId={invoice.id} onDecided={() => void refresh()} />
      <DocumentsPanel
        recordType={INVOICE}
        recordId={invoice.id}
        canAttach={can(`${INVOICE}:create`) || can(`${INVOICE}:change`)}
      />
      <RecordHistory recordType={INVOICE} recordId={invoice.id} />
      {dialog !== null && (
        <ConfirmDialog
          open
          title={dialog === 'issue' ? `Issue ${title}?` : `Submit ${title}?`}
          message={
            dialog === 'issue'
              ? 'Issuing allocates the tax-invoice number, records the VAT and posts the invoice. An issued invoice is never changed; it is corrected by a credit note.'
              : 'The invoice goes to the configured approval route and cannot be edited while it waits.'
          }
          confirmLabel={dialog === 'issue' ? 'Issue invoice' : 'Submit'}
          busy={act.isPending}
          error={act.isError ? act.error.message : null}
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={() => {
            act.mutate({ kind: dialog, version: invoice.version });
          }}
        />
      )}
    </Stack>
  );
}

/**
 * The tax invoice as issued (`/customer-invoices/:id/print`; FRD-VAT-027, VAT-030): the snapshot
 * of issuer and customer, the heading and note of its registration state, and each line's basis,
 * rate and VAT with the totals — for the browser's print. It is the issued data, never re-read
 * from the masters (MD-12).
 */
export function TaxInvoicePrintPage() {
  const { id = '' } = useParams();
  const detail = useQuery({
    queryKey: ['customer-invoice', id],
    queryFn: () => getData(`${CUSTOMER_INVOICES}/${id}`, CustomerInvoiceDetailSchema),
  });
  const back = (
    <Button component={RouterLink} to={`/customer-invoices/${id}`} startIcon={<ArrowBackIcon />}>
      Back to the invoice
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the tax invoice" />;
  if (detail.isError) {
    return <RecordFailure title="Tax invoice" noun="invoice" error={detail.error} back={back} />;
  }
  const { invoice, lines, snapshot } = detail.data;
  if (snapshot === null) {
    return (
      <Stack spacing={3}>
        <PageHeader title="Tax invoice" actions={back} />
        <Section>
          <EmptyState
            title="Not issued yet"
            detail="A tax invoice exists once the invoice is issued."
          />
        </Section>
      </Stack>
    );
  }
  const address = (...parts: (string | null)[]) => parts.filter((p) => p !== null).join(', ');
  return (
    <Stack spacing={3}>
      <PageHeader
        title={snapshot.heading}
        actions={
          <>
            {back}
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => {
                window.print();
              }}
            >
              Print
            </Button>
          </>
        }
      />
      <Section>
        <Stack spacing={2} sx={{ p: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={4}
            sx={{ justifyContent: 'space-between' }}
          >
            <Box>
              <Typography variant="overline">From</Typography>
              <Typography variant="subtitle1">{snapshot.issuerName}</Typography>
              <Typography variant="body2">
                {address(
                  snapshot.issuerAddressLine1,
                  snapshot.issuerAddressLine2,
                  snapshot.issuerCity,
                  snapshot.issuerCountry,
                )}
              </Typography>
              {snapshot.issuerTrn !== null && (
                <Typography variant="body2">TRN {snapshot.issuerTrn}</Typography>
              )}
            </Box>
            <Box>
              <Typography variant="overline">To</Typography>
              <Typography variant="subtitle1">{snapshot.customerName}</Typography>
              <Typography variant="body2">
                {address(
                  snapshot.customerAddressLine1,
                  snapshot.customerAddressLine2,
                  snapshot.customerCity,
                  snapshot.customerCountry,
                )}
              </Typography>
              {snapshot.customerTrn !== null && (
                <Typography variant="body2">TRN {snapshot.customerTrn}</Typography>
              )}
            </Box>
            <Box>
              <Typography variant="overline">{snapshot.heading}</Typography>
              <Typography variant="subtitle1">{invoice.documentNo}</Typography>
              <Typography variant="body2">Date {invoice.invoiceDate}</Typography>
              <Typography variant="body2">Tax date {invoice.taxDate}</Typography>
              {invoice.dueDate !== null && (
                <Typography variant="body2">Due {invoice.dueDate}</Typography>
              )}
            </Box>
          </Stack>
          <Divider />
          <Table size="small" aria-label="Tax invoice lines">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">Taxable amount</TableCell>
                <TableCell align="right">VAT rate %</TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line) => {
                const vat = invoice.vatCharged ? (line.vat?.vatAmount ?? '0.00') : '0.00';
                return (
                  <TableRow key={line.id}>
                    <TableCell>{line.description}</TableCell>
                    <TableCell align="right">{line.netAmount}</TableCell>
                    <TableCell align="right">
                      {invoice.vatCharged ? (line.vat?.rateApplied ?? '—') : '—'}
                    </TableCell>
                    <TableCell align="right">{vat}</TableCell>
                    <TableCell align="right">{sum([line.netAmount, vat])}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {invoice.netTotal}
                </TableCell>
                <TableCell />
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {invoice.vatCharged ? invoice.vatTotal : '0.00'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  AED {invoice.grossTotal}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {snapshot.note !== null && (
            <Typography variant="body2" color="text.secondary">
              {snapshot.note}
            </Typography>
          )}
        </Stack>
      </Section>
    </Stack>
  );
}
